import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useFormContext, useWatch } from 'react-hook-form';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Typography } from '@components/Typography';
import { Checkbox } from '@components/Checkbox';
import { Switch } from '@components/Switch';
import { useTranslation } from '@hooks';
import {
    CONTACT_FIELDS,
    contactFieldNames,
    enabledName,
    identifyingFieldsOf,
    isFieldRequired,
    requiredName,
} from './fields';

export * from './fields';

const LABEL_KEYS = {
    email: 'FeedbackSurveys.contactEmail',
    phone: 'FeedbackSurveys.contactPhone',
    name: 'FeedbackSurveys.contactName',
    sex: 'ContactsPage.sex',
    dateOfBirth: 'ContactsPage.dateOfBirth',
};

export const useContactFieldsValues = (fields = CONTACT_FIELDS, control) => {
    const context = useFormContext();
    const names = useMemo(() => contactFieldNames(fields), [fields.join()]);
    const watched = useWatch({ control: control ?? context?.control, name: names });
    return Object.fromEntries(names.map((name, index) => [name, !!watched[index]]));
};

const RequiredWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-7px)',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const FieldRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    marginTop: '8px',
    alignItems: 'center',
});

const Hint = styled(Typography, { shouldForwardProp: prop => prop !== 'hasError' })(({ theme, hasError }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: '8px',
    color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
    '& .MuiSvgIcon-root': {
        fontSize: 16,
    },
}));

const ContactFieldRow = ({
    fieldName,
    label,
    requiredLabel,
    enabled,
    required,
    disabled,
    onToggle,
    onRequiredChange,
}) => (
    <FieldRow>
        <Switch
            checked={enabled}
            handleChange={event => onToggle(fieldName, event.target.checked)}
            label={label}
            disabled={disabled}
        />
        {enabled && (
            <RequiredWrapper>
                <Checkbox
                    checked={required}
                    disabled={disabled}
                    inputProps={{ 'aria-label': `${label}: ${requiredLabel}` }}
                    onChange={event => onRequiredChange(fieldName, event.target.checked)}
                />
                <Typography variant="body2">{requiredLabel}</Typography>
            </RequiredWrapper>
        )}
    </FieldRow>
);

const ContactFieldsSettings = ({ disabled = false, fields = CONTACT_FIELDS, requireIdentifyingField = false }) => {
    const { t } = useTranslation();
    const { setValue } = useFormContext();

    const values = useContactFieldsValues(fields);

    const identifying = requireIdentifyingField ? identifyingFieldsOf(fields) : [];
    const requiredIdentifying = identifying.filter(field => isFieldRequired(values, field));
    const locked = requiredIdentifying.length === 1 ? requiredIdentifying[0] : null;
    const keepsIdentity = requiredIdentifying.length > 0;
    const requiredLabel = t('common.required');

    const setField = (name, value) => setValue(name, value, { shouldDirty: true, shouldValidate: true });

    const onToggle = (field, checked) => {
        setField(enabledName(field), checked);
        if (!checked) {
            setField(requiredName(field), false);
            return;
        }
        if (identifying.includes(field) && !keepsIdentity) {
            setField(requiredName(field), true);
        }
    };

    const onRequiredChange = (field, checked) => setField(requiredName(field), checked);

    return (
        <>
            {fields.map(field => (
                <ContactFieldRow
                    key={field}
                    fieldName={field}
                    label={t(LABEL_KEYS[field])}
                    requiredLabel={requiredLabel}
                    enabled={values[enabledName(field)]}
                    required={values[requiredName(field)]}
                    disabled={disabled || field === locked}
                    onToggle={onToggle}
                    onRequiredChange={onRequiredChange}
                />
            ))}
            {identifying.length > 0 && (
                <Hint variant="body2" hasError={!keepsIdentity} role={keepsIdentity ? undefined : 'alert'}>
                    {!keepsIdentity && <ErrorOutlineIcon />}
                    {t('ContactFieldsSettings.identifyingFieldRequired')}
                </Hint>
            )}
        </>
    );
};

export default ContactFieldsSettings;
